import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Menu, LogOut, Heart, LayoutDashboard, Dumbbell, Search, KeyRound, MessageSquare, X as CloseIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const load = () => api.get("/messages/unread_count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user]);

  const dashboardPath =
    user?.role === "admin" ? "/admin"
    : user?.role === "trainer" ? "/trainer"
    : "/dashboard";

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-200">
      <div className="container-fl h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg bg-neutral-900 text-white grid place-items-center group-hover:bg-emerald-600 transition-colors">
            <Dumbbell size={18} />
          </div>
          <span className="font-black text-lg tracking-tight">
            FitLink <span className="text-emerald-600">Ireland</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-neutral-700">
          <NavLink to="/trainers" data-testid="nav-trainers" className={({isActive}) => isActive ? "text-neutral-900" : "hover:text-neutral-900"}>Find Trainers</NavLink>
          <NavLink to="/become-a-trainer" data-testid="nav-become-trainer" className={({isActive}) => isActive ? "text-neutral-900" : "hover:text-neutral-900"}>Become a Trainer</NavLink>
          <NavLink to="/how-it-works" data-testid="nav-how-it-works" className={({isActive}) => isActive ? "text-neutral-900" : "hover:text-neutral-900"}>How It Works</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen((v) => !v)} className="md:hidden p-2 rounded-md hover:bg-neutral-100">
            {mobileOpen ? <CloseIcon size={18}/> : <Menu size={18}/>}
          </button>
          {user && (
            <button data-testid="nav-messages" onClick={() => nav("/messages")} className="relative p-2 rounded-md hover:bg-neutral-100">
              <MessageSquare size={18}/>
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] grid place-items-center">{unread}</span>}
            </button>
          )}
          {!user ? (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="btn-secondary hidden sm:inline-flex">Log in</Link>
              <Link to="/signup" data-testid="nav-signup-btn" className="btn-primary">Sign up</Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu" className="btn-secondary">
                <Menu size={16}/>
                <span className="hidden sm:inline">{user.name?.split(" ")[0] || "Account"}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator/>
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav(dashboardPath)}>
                  <LayoutDashboard size={14} className="mr-2"/> Dashboard
                </DropdownMenuItem>
                {user.role === "customer" && (
                  <DropdownMenuItem data-testid="menu-favourites" onClick={() => nav("/dashboard?tab=favourites")}>
                    <Heart size={14} className="mr-2"/> Favourites
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/account")}>
                  <KeyRound size={14} className="mr-2"/> Account & password
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}>
                  <LogOut size={14} className="mr-2"/> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div data-testid="mobile-drawer" className="md:hidden border-t border-neutral-200 bg-white">
          <div className="container-fl py-4 flex flex-col gap-1 text-sm font-medium">
            <NavLink to="/trainers" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-md hover:bg-neutral-50">Find Trainers</NavLink>
            <NavLink to="/become-a-trainer" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-md hover:bg-neutral-50">Become a Trainer</NavLink>
            <NavLink to="/how-it-works" onClick={() => setMobileOpen(false)} className="py-3 px-2 rounded-md hover:bg-neutral-50">How It Works</NavLink>
            {!user && (
              <div className="flex gap-2 pt-2 border-t border-neutral-100 mt-2">
                <NavLink to="/login" onClick={() => setMobileOpen(false)} className="btn-secondary flex-1 justify-center">Log in</NavLink>
                <NavLink to="/signup" onClick={() => setMobileOpen(false)} className="btn-primary flex-1 justify-center">Sign up</NavLink>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
